import { AppLink } from '../components/AppLink';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Icon } from '../components/Icon';
import { NotFoundContent } from '../components/NotFoundContent';
import { Picture } from '../components/Picture';
import { ProjectCard, monographLabel, projectFacts } from '../components/portfolio/ProjectCard';
import { getProject, projectsByMonograph, type Project } from '../data/projects';
import { images } from '../generated/images';
import { Seo, breadcrumbLd } from '../lib/seo';
import { SITE, absoluteUrl } from '../lib/site';
import type { PageProps } from '../routes';
import { btnOutline, btnPrimary, container, eyebrow, h2Section } from '../lib/ui';

const SECTIONS: Array<{ key: keyof Project['narrative']; title: string }> = [
  { key: 'brief', title: 'The Brief' },
  { key: 'approach', title: 'Design Approach' },
  { key: 'craft', title: 'Materials & Craft' },
  { key: 'outcome', title: 'The Outcome' },
];

/** Absolute URL of the largest JPEG rendition of an image. */
const largestJpg = (name: Project['image']) => {
  const widths = images[name].widths;
  return `/images/${name}-${widths[widths.length - 1]}.jpg`;
};

/** Up to three other projects, most shared categories first, then monograph order. */
function relatedProjects(p: Project): Project[] {
  return projectsByMonograph
    .filter((o) => o.slug !== p.slug)
    .map((o) => ({ o, score: o.categories.filter((c) => p.categories.includes(c)).length }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.o.monograph - b.o.monograph)
    .slice(0, 3)
    .map((x) => x.o);
}

export default function ProjectDetail({ params }: PageProps) {
  const project = getProject(params.slug ?? '');
  if (!project) return <NotFoundContent />;
  const p = project;

  const path = `/portfolio/${p.slug}`;
  const trail: Array<[string, string]> = [
    ['Portfolio', '/portfolio'],
    [p.title, path],
  ];
  const facts = projectFacts(p);
  const index = projectsByMonograph.findIndex((o) => o.slug === p.slug);
  const prev = projectsByMonograph[(index - 1 + projectsByMonograph.length) % projectsByMonograph.length];
  const next = projectsByMonograph[(index + 1) % projectsByMonograph.length];
  const related = relatedProjects(p);
  const heroImage = largestJpg(p.image);

  const creativeWork: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    '@id': `${absoluteUrl(path)}#project`,
    name: `${p.title} — ${p.typeLabel}`,
    description: p.seo.description,
    url: absoluteUrl(path),
    image: absoluteUrl(heroImage),
    genre: p.typeLabel,
    keywords: p.seo.keywords.join(', '),
    material: p.materials,
    creator: { '@id': `${SITE.origin}/#business` },
    isPartOf: { '@type': 'CollectionPage', url: absoluteUrl('/portfolio') },
  };
  if (p.facts?.location) creativeWork.locationCreated = { '@type': 'Place', name: p.facts.location };
  if (p.facts?.completed) creativeWork.dateCreated = p.facts.completed;

  return (
    <>
      <Seo
        title={p.seo.title}
        description={p.seo.description}
        keywords={p.seo.keywords}
        path={path}
        image={heroImage}
        jsonLd={[breadcrumbLd(trail), creativeWork]}
      />

      {/* HERO */}
      <section className="w-full bg-surface-ivory pt-space-lg md:pt-space-xl pb-space-xl">
        <div className={`${container} flex flex-col gap-space-lg`}>
          <Breadcrumbs trail={trail} />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter lg:items-end">
            <div className="lg:col-span-5 flex flex-col gap-space-md">
              <div className="flex items-center gap-space-xs">
                <span className="w-8 h-px bg-secondary" />
                <span className="font-label-sm text-label-sm uppercase tracking-widest text-secondary">
                  {monographLabel(p.monograph)} · {p.tag}
                </span>
              </div>
              <h1 className="flex flex-col gap-space-xs animate-rise">
                <span className="font-headline-xl text-headline-xl-mobile md:text-headline-xl text-primary font-normal tracking-tight !leading-[1.1]">
                  {p.title}
                </span>
                <span className="font-headline-sm text-headline-sm text-secondary italic">
                  <span className="sr-only">— </span>
                  {p.typeLabel}
                </span>
              </h1>
              <p className="font-body-lg text-body-lg text-on-surface-variant animate-rise [animation-delay:80ms]">{p.summary}</p>
              <div className="flex flex-wrap gap-space-sm pt-space-xs">
                <AppLink href="/contact" className={btnPrimary}>
                  <span>Discuss a Similar Project</span>
                  <Icon name="arrow_forward" size={18} />
                </AppLink>
                <a href="#gallery" className={btnOutline}>
                  View Gallery
                </a>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="relative rounded-xl overflow-hidden shadow-xl bg-surface-container-high aspect-[4/3] lg:aspect-[16/11]">
                <Picture
                  name={p.image}
                  alt={p.imageAlt}
                  sizes="(min-width: 1024px) 58vw, 100vw"
                  priority
                  className="block w-full h-full"
                  imgClassName="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-botanical-dark/40 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>
          </div>

          {facts.length > 0 && (
            <dl
              className={`grid grid-cols-2 ${facts.length >= 4 ? 'md:grid-cols-4' : facts.length === 3 ? 'md:grid-cols-3' : ''} gap-space-md bg-surface-cream rounded-lg p-space-md md:p-space-lg`}
            >
              {facts.map(([label, value]) => (
                <div key={label} className="flex flex-col gap-1">
                  <dt className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">{label}</dt>
                  <dd className="font-headline-sm text-[1.15rem] md:text-headline-sm text-primary">{value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </section>

      {/* NARRATIVE + MATERIALS */}
      <section className="w-full bg-surface py-space-xl md:py-20">
        <div className={`${container} grid grid-cols-1 lg:grid-cols-12 gap-space-xl lg:gap-gutter`}>
          <div className="lg:col-span-7 flex flex-col gap-space-xl">
            {SECTIONS.map((s, i) => (
              <article key={s.key} className="reveal grid grid-cols-[auto_1fr] gap-x-space-md md:gap-x-space-lg">
                <span className="font-label-md text-label-md text-gold-dark tracking-widest pt-2" aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="flex flex-col gap-space-sm">
                  <h2 className="font-headline-md text-headline-md text-primary">{s.title}</h2>
                  {p.narrative[s.key].map((para) => (
                    <p key={para.slice(0, 32)} className="font-body-lg text-body-md md:text-body-lg text-on-surface-variant">
                      {para}
                    </p>
                  ))}
                </div>
              </article>
            ))}
          </div>

          <aside className="lg:col-span-4 lg:col-start-9">
            <div className="lg:sticky lg:top-28 flex flex-col gap-space-md">
              <div className="bg-surface-cream rounded-xl p-space-lg flex flex-col gap-space-md shadow-sm">
                <span className={eyebrow}>Material Integrity</span>
                <h2 className="font-headline-sm text-headline-sm text-primary -mt-space-sm">Material Palette</h2>
                <ul className="flex flex-col divide-y divide-outline-variant/50">
                  {p.materials.map((m) => (
                    <li key={m} className="flex items-center gap-space-sm py-space-sm font-body-md text-body-md text-on-surface">
                      <Icon name="texture" size={18} className="text-emerald-vivid" />
                      {m}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-botanical-dark text-on-primary rounded-xl p-space-lg flex flex-col gap-space-sm">
                <p className="font-headline-sm text-headline-sm text-surface-ivory">Planning something similar?</p>
                <p className="font-body-sm text-body-sm text-surface-ivory/75">
                  Share your floor plan and brief — our Nagercoil studio will walk you through layout, materials and
                  scope.
                </p>
                <a
                  href={SITE.whatsappUrl}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-space-xs font-label-md text-label-md uppercase tracking-wider text-gold-light hover:text-surface-ivory transition-colors pt-space-xs"
                >
                  <Icon name="chat" size={18} /> WhatsApp {SITE.phoneDisplay}
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="w-full bg-surface-cream py-space-xl md:py-20">
        <div className={container}>
          <div className="reveal mb-space-lg">
            <span className={eyebrow}>Visual Record</span>
            <h2 className={`${h2Section} mt-space-xs`}>Project Gallery</h2>
          </div>
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${p.gallery.length >= 3 ? 'lg:grid-cols-3' : ''} gap-space-md md:gap-gutter`}>
            {p.gallery.map((g) => (
              <figure key={g.image} className="reveal relative rounded-xl overflow-hidden shadow-md h-72 md:h-80 bg-surface-container-high">
                <Picture
                  name={g.image}
                  alt={g.alt}
                  sizes={p.gallery.length >= 3 ? '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw' : '(min-width: 640px) 50vw, 100vw'}
                  className="block w-full h-full"
                  imgClassName="w-full h-full object-cover"
                />
                <figcaption className="absolute bottom-0 inset-x-0 p-space-sm pt-space-xl bg-gradient-to-t from-botanical-dark/90 to-transparent font-label-sm text-label-sm uppercase tracking-wider text-gold-light">
                  {g.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* PREVIOUS / NEXT */}
      <nav aria-label="More case studies" className="w-full bg-surface-ivory border-y border-outline-variant/40">
        <div className={`${container} grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-outline-variant/50`}>
          <AppLink href={`/portfolio/${prev.slug}`} rel="prev" className="group flex items-center gap-space-md py-space-lg sm:pr-space-lg">
            <Icon name="arrow_back" size={22} className="text-secondary group-hover:-translate-x-1 transition-transform" />
            <span className="flex flex-col">
              <span className="font-label-sm text-label-sm uppercase tracking-widest text-on-surface-variant">
                Previous · {monographLabel(prev.monograph)}
              </span>
              <span className="font-headline-sm text-headline-sm text-primary group-hover:text-secondary transition-colors">{prev.title}</span>
            </span>
          </AppLink>
          <AppLink
            href={`/portfolio/${next.slug}`}
            rel="next"
            className="group flex items-center justify-end text-right gap-space-md py-space-lg sm:pl-space-lg"
          >
            <span className="flex flex-col">
              <span className="font-label-sm text-label-sm uppercase tracking-widest text-on-surface-variant">
                Next · {monographLabel(next.monograph)}
              </span>
              <span className="font-headline-sm text-headline-sm text-primary group-hover:text-secondary transition-colors">{next.title}</span>
            </span>
            <Icon name="arrow_forward" size={22} className="text-secondary group-hover:translate-x-1 transition-transform" />
          </AppLink>
        </div>
      </nav>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="w-full bg-surface py-space-xl md:py-20">
          <div className={container}>
            <div className="reveal flex flex-col md:flex-row md:items-end justify-between gap-space-md mb-space-lg">
              <div>
                <span className={eyebrow}>Continue the Archive</span>
                <h2 className={`${h2Section} mt-space-xs`}>Related Case Studies</h2>
              </div>
              <AppLink
                href="/portfolio"
                className="font-label-md text-label-md uppercase tracking-wider text-primary hover:text-secondary flex items-center gap-1 transition-colors"
              >
                <span>View All Works</span>
                <Icon name="arrow_forward" size={16} />
              </AppLink>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-gutter">
              {related.map((r) => (
                <li key={r.slug} className="reveal flex">
                  <ProjectCard project={r} headingLevel="h3" className="w-full" />
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="w-full bg-botanical-dark text-on-primary py-space-xl md:py-20 relative overflow-hidden">
        <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-tertiary-container/30 blur-3xl pointer-events-none" />
        <div className="absolute left-1/4 -top-24 w-80 h-80 rounded-full bg-emerald-vivid/10 blur-3xl pointer-events-none" />
        <div className={`${container} relative z-10`}>
          <div className="reveal bg-surface-cream/5 backdrop-blur-md rounded-2xl p-space-md sm:p-space-lg md:p-space-xl flex flex-col lg:flex-row lg:items-center justify-between gap-space-lg">
            <div className="flex flex-col gap-space-sm max-w-2xl">
              <div className="flex items-center gap-space-xs">
                <span className="w-6 h-px bg-gold-light" />
                <span className="font-label-sm text-label-sm uppercase tracking-widest text-gold-light">Direct Studio Engagement</span>
              </div>
              <h2 className="font-headline-lg text-headline-lg-mobile md:text-headline-lg text-on-primary leading-tight">
                Begin Your Own Spatial Transformation
              </h2>
              <p className="font-body-lg text-body-lg text-gold-light/80">
                Tell us about your home, office or restaurant in Nagercoil, Kanyakumari, Trivandrum or beyond. We will
                review your plans and suggest a design direction suited to your space and climate.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col gap-space-sm shrink-0">
              <AppLink
                href="/contact"
                className="inline-flex items-center justify-center gap-space-sm bg-gold-light text-primary hover:bg-surface-ivory font-label-lg text-label-lg uppercase tracking-wider px-space-lg py-space-md rounded transition-all duration-300"
              >
                <Icon name="calendar_today" size={18} />
                <span>Book a Consultation</span>
              </AppLink>
              <a
                href={`tel:${SITE.phoneE164}`}
                className="inline-flex items-center justify-center gap-space-sm border border-gold-light/40 text-surface-ivory hover:bg-primary-container font-label-lg text-label-lg uppercase tracking-wider px-space-lg py-space-md rounded transition-all duration-300"
              >
                <Icon name="call" size={18} className="text-gold-light" />
                <span>{SITE.phoneDisplay}</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
